import axiosInstance from '@/services/axiosInstance'
import { AdditionalCostRequest, ObjectTypeCode, RequestStatusCode } from '@/types/AdditionalCostRequest'
import AdditionalCostRequestStatus from '@/constants/AdditionalCostRequestStatus'
import { PaginationParams } from '@/types/filter/PaginationParams'

// 5 filter phát sinh thêm (0055-loc-nguoi-gui-otc-bod-ngay-de-xuat-ngay-den-diem-dong-hang.md):
// requested_by/reviewed_by/approved_by lọc theo đúng username (không phải user_id), match tuyệt đối.
// requested_at/vn_to_loading_point_date lọc theo đúng 1 ngày (yyyy-MM-dd), BE tự quy về khoảng
// [00:00:00.000, 23:59:59.999] của ngày đó. vn_to_loading_point_date chỉ tồn tại trên VehicleTracking
// -> khi đang lọc theo field này, mọi đề xuất object_type=CUSTOMS_DECLARATION tự động bị loại (hành vi
// mong đợi từ BE, không phải lỗi).
export type AdditionalCostRequestFilters = {
  object_type?: ObjectTypeCode | null
  object_id?: number | null
  status?: RequestStatusCode | null
  code?: string | null
  requested_by?: string | null
  reviewed_by?: string | null
  approved_by?: string | null
  requested_at?: string | null
  vn_to_loading_point_date?: string | null
}

export const apiGetAdditionalCostRequestList = async (params: AdditionalCostRequestFilters) => {
  const response = await axiosInstance.get('/api/v1/additional-cost-requests/list', { params })
  return response.data
}

export const apiGetAdditionalCostRequestPagination = async (
  filters: AdditionalCostRequestFilters,
  pagination: PaginationParams,
) => {
  const { page, size } = pagination
  const response = await axiosInstance.get('/api/v1/additional-cost-requests/pagination', {
    params: { ...filters, page, size },
  })
  return response.data
}

export const apiGetAdditionalCostRequest = async (id: number) => {
  const response = await axiosInstance.get(`/api/v1/additional-cost-requests/${id}`)
  return response.data
}

export const apiCreateAdditionalCostRequest = async (data: {
  object_id: number
  object_type: ObjectTypeCode
  amount: number
  reason: string
  input_arise?: string | null
  output_arise?: string | null
}) => {
  const response = await axiosInstance.post('/api/v1/additional-cost-requests', data, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

export const apiUpdateAdditionalCostRequest = async (
  id: number,
  data: {
    object_id: number
    object_type: ObjectTypeCode
    amount: number
    reason: string
    input_arise?: string | null
    output_arise?: string | null
  },
) => {
  const response = await axiosInstance.patch(`/api/v1/additional-cost-requests/${id}`, data, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

export const apiProcessAdditionalCostRequest = async (
  id: number,
  data: {
    status:
      | typeof AdditionalCostRequestStatus.PENDING_BOD
      | typeof AdditionalCostRequestStatus.REJECTED_BY_OCT
      | typeof AdditionalCostRequestStatus.APPROVED_BY_BOD
      | typeof AdditionalCostRequestStatus.REJECTED_BY_BOD
    note?: string | null
  },
) => {
  const response = await axiosInstance.patch(`/api/v1/additional-cost-requests/${id}/process`, data, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

// Cập nhật riêng 3 ô ghi chú "Ghi chú người gửi" / "Ghi chú OCT" / "Ghi chú BOD" (`request_note` /
// `review_note` / `approve_note`) sửa thẳng trên bảng danh sách. KHÁC hẳn PATCH /{id}/process: endpoint
// này chỉ đụng đúng các cột đó, không yêu cầu `status`/role, không ảnh hưởng luồng thẩm định/phê duyệt
// chính thức. Key vắng mặt = giữ nguyên giá trị cũ, value null hoặc chuỗi rỗng = xoá ghi chú.
// Xem docs/for-backend/0010-ghi-chu-otc-bod-de-xuat-phat-sinh.md.
export const apiUpdateAdditionalCostRequestExtra = async (
  id: number,
  data: { request_note?: string | null; review_note?: string | null; approve_note?: string | null },
) => {
  const response = await axiosInstance.patch(`/api/v1/additional-cost-requests/${id}/extra`, data, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

export const apiDeleteAdditionalCostRequest = async (id: number) => {
  const response = await axiosInstance.delete(`/api/v1/additional-cost-requests/${id}`)
  return response.data
}

export const apiBulkDeleteAdditionalCostRequests = async (ids: number[]) => {
  const response = await axiosInstance.delete('/api/v1/additional-cost-requests/batch', {
    data: ids,
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

// Số đếm badge cạnh menu "Đề xuất phát sinh" - xem docs/receive-backend/0017-badge-de-xuat-phat-sinh.md.
export interface AdditionalCostRequestNotificationCounts {
  oct_count: number
  bod_count: number
  approved_count: number
}

export const apiGetAdditionalCostRequestNotificationCounts = async () => {
  const response = await axiosInstance.get('/api/v1/additional-cost-requests/notification-counts')
  return response.data
}

// Đánh dấu đã đọc TOÀN BỘ approved_count liên quan tới user hiện tại - gọi 1 lần ngay sau khi menu
// "Đề xuất phát sinh" mở xong (load /pagination xong), không cần id/body.
export const apiMarkAdditionalCostRequestNotificationsRead = async () => {
  const response = await axiosInstance.patch('/api/v1/additional-cost-requests/mark-notifications-read')
  return response.data
}

export type { AdditionalCostRequest }
